import {css} from '@emotion/css';
import {Constants, reperchMyCollectionFullUrl} from '@remoov/functional';
import {
  Button,
  FacebookIcon,
  facebookShareUrl,
  PinterestIcon,
  pinterestShareUrl,
  PublicReviewButtons,
  theme,
  TwitterIcon,
  twitterShareUrl,
  WarningBanner,
} from '@remoov/remoov-components';
import React, {AnchorHTMLAttributes, HTMLAttributes} from 'react';
import {Link} from 'react-router-dom';
import {BoldInfoText, DashboardCard, InfoText} from './Common';

const {
  NewCustomerPortalUrl: {BETA_ITEMS_URL},
} = Constants;

export type IntroCardProps = {
  customerName: string;
  expiredItems?: boolean;
  expiredItemsLink: () => void | Promise<void>;
  requestPublicReview?: boolean;
  market: Maybe<string>;
  encryptedId: Maybe<string>;
  ownerId: Maybe<number>;
  showMyCollection: boolean;
};

export const IntroCard = ({
  customerName,
  expiredItems = false,
  expiredItemsLink,
  requestPublicReview = false,
  market,
  encryptedId,
  ownerId,
  showMyCollection,
}: IntroCardProps) => (
  <Container>
    <Title>Hi {customerName},</Title>
    <SubText>
      Welcome to your personalized dashboard where you can view the status of each of your items!
      Remember to add notes to your items to maximize their resale value. You will find each item
      listed in the <Link to={BETA_ITEMS_URL}>My Items</Link> section.
    </SubText>
    {!!requestPublicReview && (
      <>
        <SubText>
          Our team works hard to provide great service making it easy to declutter and reduce waste.
          As a small business, positive reviews mean the world to us. We{"'"}d really appreciate it
          if you could share it on Yelp, Google or Nextdoor. Your feedback makes a huge difference!
          And mention your movers as it means a lot to them!
        </SubText>
        <PublicReviewButtons encryptedId={encryptedId} market={market} />
      </>
    )}

    {!!expiredItems && (
      <WarningBanner>
        <BoldInfoText>
          You have expired items, <WarningLink onClick={expiredItemsLink}> click here</WarningLink>{' '}
          <InfoText>to review them, and select an action.</InfoText>
        </BoldInfoText>
      </WarningBanner>
    )}
    {showMyCollection && (
      <>
        {!!expiredItems && (
          <div
            className={css({
              height: theme.spacings.small,
            })}
          />
        )}
        <YourCollectionBanner ownerId={ownerId} />
      </>
    )}
  </Container>
);
const YourCollectionBanner = ({ownerId}: {ownerId: Maybe<number>}) => {
  const url = reperchMyCollectionFullUrl(String(ownerId || ''));
  const textContent = `I recently decluttered with Remoov, and now my items are up for sale on Reperch!
📷📷 Check Out My Collection on Reperch 📷📷: ${url}
Shopping secondhand great to save money and reduce waste! 📷📷
#ShopSustainably #DeclutterWithRemoov #Reperch`;
  return (
    <div
      className={css({
        width: '100%',
        background: `${theme.colors.reperch_primary_lighter}`,
        borderRadius: 12,
        border: `1px solid ${theme.colors.reperch_primary_darker}`,
        padding: theme.spacings.medium,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        [theme.mediaMaxWidth.medium]: {flexDirection: 'column'},
      })}
    >
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          [theme.mediaMaxWidth.medium]: {alignItems: 'center'},
        })}
      >
        <BoldInfoText
          className={css({
            fontSize: theme.font.sizes.xsmall,
            fontWeight: theme.font.bold,
            color: theme.colors.orange,
            width: 64,
            height: 22,
            background: theme.colors.white,
            borderRadius: 16,
            padding: `2px 8px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacings.xxsmall,
            [theme.mediaMaxWidth.medium]: {marginBottom: 8},
          })}
        >
          NEW!⚡️
        </BoldInfoText>
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            [theme.mediaMaxWidth.medium]: {flexDirection: 'row', alignItems: 'baseline'},
          })}
        >
          <InfoText
            className={css({
              fontSize: theme.font.sizes.medium,
              fontWeight: 700,
              color: theme.colors.reperch_primary,
              [theme.mediaMaxWidth.medium]: {marginRight: theme.spacings.xxsmall},
            })}
          >
            Your Collection
          </InfoText>
          <InfoText
            className={css({
              fontSize: theme.font.sizes.small,
              color: theme.colors.reperch_primary_darker,
            })}
          >
            on Reperch
          </InfoText>
        </div>
      </div>
      <Button
        buttonStyle='secondary'
        className={css({
          width: 126,
          background: 'transparent',
          margin: `0 ${theme.spacings.xsmall}`,
          [theme.mediaMaxWidth.medium]: {margin: `${theme.spacings.xxsmall} 0`},
        })}
        as='a'
        target='_blank'
        href={url}
      >
        <InfoText className={css({color: theme.colors.darkBlue, fontWeight: 600})}>View</InfoText>
      </Button>
      <div>
        <span
          className={css({
            fontWeight: 700,
            color: theme.colors.darkGray,
            fontSize: theme.font.sizes.small,
          })}
        >
          Share this Collection:
        </span>
        <div
          className={css({
            display: 'flex',
            marginTop: theme.spacings.xsmall,
            width: '100%',
            justifyContent: 'space-evenly',
          })}
        >
          <a href={twitterShareUrl(textContent)} target='_blank' rel='noopener noreferrer'>
            <TwitterIcon />
          </a>
          <a
            href={facebookShareUrl(url)}
            target='_blank'
            rel='noopener noreferrer'
            className={css({margin: `0 ${theme.spacings.xxsmall}`})}
          >
            <FacebookIcon />
          </a>
          <a
            href={pinterestShareUrl(textContent, url, '')}
            target='_blank'
            rel='noopener noreferrer'
          >
            <PinterestIcon />
          </a>
        </div>
      </div>
    </div>
  );
};
const Container = (args: HTMLAttributes<HTMLDivElement>) => (
  <DashboardCard
    className={css({
      background: 'transparent',
      padding: `0 ${theme.spacings.xxsmall}`,
      [theme.mediaMaxWidth.small]: {padding: 0},
    })}
    {...args}
  />
);
const Title = (args: HTMLAttributes<HTMLParagraphElement>) => (
  <BoldInfoText
    className={css({
      fontSize: theme.font.sizes.large,
      marginBottom: theme.spacings.large,
      marginTop: theme.spacings.xsmall,
    })}
    {...args}
  />
);
const SubText = (args: HTMLAttributes<HTMLParagraphElement>) => (
  <InfoText
    className={css({
      maxWidth: '480px',
      marginBottom: theme.spacings.small,
      [theme.mediaMaxWidth.medium]: {marginBottom: theme.spacings.small},
      [theme.mediaMaxWidth.small]: {marginBottom: theme.spacings.xsmall},
    })}
    {...args}
  />
);
const WarningLink = (args: AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a
    className={css({
      color: theme.colors.darkGray,
      fontSize: theme.font.sizes.small,
      fontFamily: theme.font.family,
      fontWeight: theme.font.bold,
      textAlign: 'left',
      textDecoration: 'underline',
      cursor: 'pointer',
    })}
    {...args}
  />
);
